import { Component, effect, inject, type OnInit } from "@angular/core";

import { CommonModule } from "@angular/common";

import { MatDialog } from "@angular/material/dialog";

import { EmptyComponent } from "../../../shared/components/empty/empty.component";

import { GloControlComponent } from "../../../shared/components/glo-control/glo-control.component";

import { ConfirmDialogComponent } from "../../../shared/components/confirm-dialog/confirm-dialog.component";

import { LoaderComponent } from "../../../shared/components/loader/loader.component";

import { GloPaginationComponent } from "../../../shared/components/pagination/pagination.component";

import { Salon } from "../../../shared/models/salon.model";

import { Service } from "../../../shared/models/service.model";

import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";

import { ApiService } from "../../../shared/services/api/api.service";

import { BusinessSalonContextService } from "../../../shared/services/api/business-salon-context.service";

import { environment } from "../../../../environments/environment";

import { BusinessServiceCreateComponent } from "./create/create.component";

@Component({
  selector: "business-services-main",

  standalone: true,

  imports: [
    CommonModule,
    EmptyComponent,
    GloControlComponent,
    LoaderComponent,
    GloPaginationComponent,
  ],

  templateUrl: "./services.component.html",

  styleUrl: "./services.component.scss",
})
export class BusinessServicesMainComponent implements OnInit {
  private dialog = inject(MatDialog);

  private apiService = inject(ApiService);

  readonly salonContext = inject(BusinessSalonContextService);

  assetUrl = environment.assetBaseUrl;

  services: Service[] = [];

  /*
   * These are only the salons currently selected
   * in the global sidebar context.
   */
  salons: Salon[] = [];

  loading = false;

  filteredServices: Service[] = [];

  viewServices: Service[] = [];

  currentPage = 1;

  itemsPerPage = 10;

  /*
   * Used to avoid stale async results if the owner
   * changes salon selection while requests are running.
   */
  private loadVersion = 0;

  constructor() {
    /*
     * React automatically whenever the sidebar salon
     * selection changes.
     */
    effect(() => {
      const allSalons = this.salonContext.salons();

      const selectedIds = this.salonContext.selectedSalonIds();

      /*
       * BusinessComponent initializes the context.
       * Avoid loading until that has happened.
       */
      if (!allSalons.length) {
        return;
      }

      this.syncSelectedSalons(allSalons, selectedIds);

      this.loadAllServices();
    });
  }

  ngOnInit(): void {
    /*
     * No initial load here.
     *
     * The salon-context effect handles the first load
     * once BusinessComponent has initialized the salons.
     */
  }

  /* =====================================================
     KPIs
     ===================================================== */

  get assignedSalonCount(): number {
    const salonIds = new Set(
      this.services
        .map((service: any) => String(service.salon_id ?? service.salon ?? ""))
        .filter(Boolean),
    );

    return salonIds.size;
  }

  get averageServicePrice(): number {
    if (!this.services.length) {
      return 0;
    }

    const total = this.services.reduce((sum, service: any) => {
      const price = Number(service.price || 0);

      return sum + (Number.isFinite(price) ? price : 0);
    }, 0);

    return total / this.services.length;
  }

  get averageServiceDuration(): number {
    if (!this.services.length) {
      return 0;
    }

    const total = this.services.reduce((sum, service: any) => {
      const duration = Number(service.duration_minutes ?? 30);

      return sum + (Number.isFinite(duration) ? duration : 30);
    }, 0);

    return Math.round(total / this.services.length);
  }

  /* =====================================================
     Global salon context
     ===================================================== */

  private syncSelectedSalons(allSalons: any[], selectedIds: string[]): void {
    const selectedSet = new Set(selectedIds.map(String));

    /*
     * If no salons are selected globally,
     * this page should show no services.
     */
    if (!selectedSet.size) {
      this.salons = [];
      return;
    }

    this.salons = allSalons
      .filter((salon: any) => selectedSet.has(String(salon.salon_id)))
      .map(
        (salon: any) =>
          ({
            ...salon,
          }) as Salon,
      );
  }

  /* =====================================================
     Load services for selected salons
     ===================================================== */

  loadAllServices(): void {
    /*
     * Increment for every reload so older async responses
     * cannot overwrite a newer salon selection.
     */
    const version = ++this.loadVersion;

    this.loading = true;

    /*
     * Nothing selected means nothing to load.
     */
    if (!this.salons.length) {
      this.services = [];
      this.filteredServices = [];
      this.viewServices = [];
      this.currentPage = 1;
      this.loading = false;

      return;
    }

    const requests = this.salons.map((salon: any) =>
      this.apiService
        .request(
          API_ENDPOINTS.salons.services.list(String(salon.salon_id)),

          "GET",

          null,
        )
        .toPromise()
        .then((response: any) => {
          const results = Array.isArray(response)
            ? response
            : (response?.results ??
              response?.data?.results ??
              response?.data ??
              []);

          return (Array.isArray(results) ? results : []).map(
            (service: any) => ({
              ...service,

              salon_name: salon.name,

              salon_id: salon.salon_id,
            }),
          );
        })
        .catch((error) => {
          console.error("Error loading services for salon:", salon.name, error);

          return [];
        }),
    );

    Promise.all(requests)
      .then((allServices) => {
        /*
         * Ignore this response if another salon-selection
         * reload began while these requests were running.
         */
        if (version !== this.loadVersion) {
          return;
        }

        this.services = allServices.flat();

        this.filteredServices = [...this.services];

        this.currentPage = 1;

        this.updatePaginatedServices();

        this.loading = false;
      })
      .catch((error) => {
        if (version !== this.loadVersion) {
          return;
        }

        console.error("Error loading services:", error);

        this.services = [];
        this.filteredServices = [];
        this.viewServices = [];

        this.loading = false;
      });
  }

  /* =====================================================
     Add service
     ===================================================== */

  openServiceAdd(): void {
    const dialogRef = this.dialog.open(BusinessServiceCreateComponent, {
      panelClass: ["crm-detail-dialog"],

      data: {
        service_id: null,

        /*
         * Useful for your create modal if you decide
         * to preselect salons later.
         */
        selectedSalonIds: this.salonContext.selectedSalonIds(),
      },

      width: "760px",

      maxWidth: "100vw",

      height: "100vh",

      position: {
        right: "0",
        top: "0",
      },
    });

    dialogRef.afterClosed().subscribe((refresh) => {
      if (refresh) {
        this.loadAllServices();
      }
    });
  }

  /* =====================================================
     Edit
     ===================================================== */

  openServiceEdit(service: Service): void {
    const dialogRef = this.dialog.open(BusinessServiceCreateComponent, {
      panelClass: ["crm-detail-dialog"],

      data: {
        ...service,
      },

      width: "760px",

      maxWidth: "100vw",

      height: "100vh",

      position: {
        right: "0",
        top: "0",
      },
    });

    dialogRef.afterClosed().subscribe((refresh) => {
      if (refresh) {
        this.loadAllServices();
      }
    });
  }

  /* =====================================================
     Delete
     ===================================================== */

  deleteService(service: Service): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: "Delete Service",

        message: `Are you sure you want to delete "${service.name}"?`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.loading = true;

      this.apiService
        .request(
          API_ENDPOINTS.salons.services.deleteService(
            String((service as any).salon_id),

            String((service as any).service_id),
          ),

          "DELETE",

          null,

          "Service Deleted Successfully",
        )
        .subscribe({
          next: () => {
            this.loadAllServices();
          },

          error: (error) => {
            console.error("Error deleting service:", error);

            this.loading = false;
          },
        });
    });
  }

  /* =====================================================
     Search
     ===================================================== */

  onSearch(searchValue: string): void {
    const term = (searchValue || "").toLowerCase().trim();

    this.filteredServices = term
      ? this.services.filter((service: any) => {
          const name = String(service.name ?? "").toLowerCase();

          const salonName = String(service.salon_name ?? "").toLowerCase();

          return name.includes(term) || salonName.includes(term);
        })
      : [...this.services];

    this.currentPage = 1;

    this.updatePaginatedServices();
  }

  /* =====================================================
     Pagination
     ===================================================== */

  updatePaginatedServices(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;

    const endIndex = startIndex + this.itemsPerPage;

    this.viewServices = this.filteredServices.slice(startIndex, endIndex);
  }

  onPageChange(page: number): void {
    this.currentPage = page;

    this.updatePaginatedServices();
  }

  onItemsPerPageChange(limit: number): void {
    this.itemsPerPage = limit;

    this.currentPage = 1;

    this.updatePaginatedServices();
  }

  /* =====================================================
     Display helpers
     ===================================================== */

  getServiceInitial(service: any): string {
    return (service.name || "S").charAt(0).toUpperCase();
  }

  getServiceDuration(service: any): number {
    const duration = Number(service.duration_minutes ?? 30);

    return Number.isFinite(duration) ? duration : 30;
  }

  getServiceValue(service: any, fieldName: string): string {
    return String(service?.[fieldName] ?? "");
  }

  toMoney(value: any): string {
    const amount = Number(value);

    return new Intl.NumberFormat("en-US", {
      style: "currency",

      currency: "USD",
    }).format(Number.isFinite(amount) ? amount : 0);
  }
}
